/// <amd-module name="Wit/WorkItemTFVCLinks/Components/ItemPathInput" />
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { usePageContext } from "VSS/Platform/Layout";
import { IObservableValue, useObservable, useSubscription } from "VSS/Core/Observable";
import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { IListBoxItem } from "VSSUI/ListBox";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Observer } from "VSSUI/Observer";
import { getTfvcClient } from "VC/Common/RestClient/Tfvc";
import { TfvcItem, VersionControlRecursionType } from "VC/Common/Contracts/Git";
import * as Resources from "Wit/WorkItemTFVCLinks/Resources";
import { separateRoot } from "../TFVCLinkUtils";

export interface IItemPathInputProps {
    className?: string;
    itemPath: string;
    onItemPathChanged: (itemPath: string) => void;
    errorMessage?: IObservableValue<string | undefined>;
}

/**
 * Shows a pair of form controls that, together, allow the user to select an item path.
 * The left control is a dropdown for choosing the root path.
 * The right control is a text field for entering the rest of the path.
 */
export function ItemPathInput({ className, itemPath, onItemPathChanged, errorMessage }: IItemPathInputProps) {
    const rootPathOptions = useRootPathOptions();
    const rootPathSelection = useMemo(() => new DropdownSelection(), []);
    const [rootPath, setRootPath] = useState<string | undefined>();
    const [restPath, setRestPath] = useObservable<string>("");

    /**
     * When the root path options change (i.e. when they load), select the root path that matches
     * the current path if it exists. Otherwise, select the first option.
     */
    useEffect(() => {
        if (rootPathOptions.length === 0) {
            // There are no options, so there is nothing to do
            return;
        }

        const matchingIndex = rootPathOptions.findIndex(o => o.id === rootPath);
        if (matchingIndex > -1) {
            // One of the options matches the current path. Select it.
            rootPathSelection.select(matchingIndex);
            return;
        }

        // Either there is no current path, or it does not match the options. Just select the first option.
        rootPathSelection.select(0);
        setRootPath(rootPathOptions[0].id);
    }, [rootPathOptions]);

    /**
     * When the item path prop changes, update our internal state to match it.
     */
    useEffect(() => {
        const [root, rest] = separateRoot(itemPath);
        const rootIndex = rootPathOptions.findIndex(o => o.id === root);
        if (root && root !== rootPath && rootIndex > -1) {
            rootPathSelection.select(rootIndex);
            setRootPath(root);
        }
        if (rest) {
            setRestPath(rest);
        }
    }, [itemPath]);

    /**
     * When the value of the root path dropdown changes, construct the full path and fire the callback
     */
    useEffect(() => {
        if (!rootPath) {
            return;
        }
        const newPath = rootPath + "/" + restPath.value;
        if (newPath !== itemPath) {
            onItemPathChanged(newPath);
        }
    }, [rootPath, onItemPathChanged]);

    /**
     * When the value of the text field changes, construct the full path and fire the callback
     */
    useSubscription(
        restPath,
        rest => {
            const newPath = rootPath + "/" + rest;
            if (newPath !== itemPath) {
                onItemPathChanged(newPath);
            }
        },
        [rootPath, onItemPathChanged]
    );

    return (
        <Observer errorMessage={errorMessage}>
            {({ errorMessage }: { errorMessage: string | undefined }) => (
                <FormItem className={className} label={Resources.ItemPathLabel} error={!!errorMessage} message={errorMessage}>
                    <div className="flex-row flex-center">
                        <Dropdown
                            className="item-path-input-root-path"
                            containerClassName="flex-grow"
                            disabled={rootPathOptions.length === 0}
                            items={rootPathOptions}
                            selection={rootPathSelection}
                            onSelect={(e, item) => setRootPath(item.id)}
                        />
                        <span className="font-size-l margin-horizontal-4">{"/"}</span>
                        <TextField
                            className="item-path-input-rest-path"
                            containerClassName="flex-grow"
                            value={restPath}
                            onChange={(e, val) => setRestPath(val)}
                        />
                    </div>
                </FormItem>
            )}
        </Observer>
    );
}

function useRootPathOptions(): IListBoxItem[] {
    const pageCoontext = usePageContext();
    const client = useMemo(() => getTfvcClient(pageCoontext), [pageCoontext]);

    const [rootPathOptions, setRootPathOptions] = useState<IListBoxItem[]>([]);

    useEffect(() => {
        client.getItem("", undefined, undefined, false, "$/", VersionControlRecursionType.OneLevel, undefined, false).then(response => {
            const tfvcItems = (response as unknown) as TfvcItem[]; // The typing for the API is wrong - if using scopePath and recursionLevel = OneLevel, it returns an array.
            const listItems = tfvcItems
                .filter(item => item.path !== "$/")
                .map(item => ({
                    text: item.path,
                    id: item.path
                }));

            setRootPathOptions(listItems);
        });
    }, [client]);

    return rootPathOptions;
}
